import styled from '@emotion/styled'
import { useEffect, useState } from 'react'

import { SimpleTabStrip, Space, ValidationMessage } from '../../../components/core-v2'
import StepTab from '../../../components/core-v2/molecules/step-tab'
import FundsDistribution, {
  ExDateDistribution
} from '../../../components/core-v2/organisms/funds-distribution'
import FundsReinvestment, {
  ExDateReinvestment,
  ExpectedReinvestmentData
} from '../../../components/core-v2/organisms/funds-reinvestment'
import { Errors } from '../../tradebuilder-v2/util'
import { ExDateSchedule } from '../types/exDateSchedule'

export type FundExDateUpcomingFormProps = {
  errors: Errors
  exDate: ExDateSchedule
  expectedReinvestmentData: ExpectedReinvestmentData
  onDistributionSubmit: (updatedDistribution: ExDateDistribution) => void
  onReinvestmentSubmit: (updatedReinvestment: ExDateReinvestment) => void
}

enum ExDateTab {
  Distribution = 1,
  Reinvestment = 2,
  Complete = 3
}

const FundExDateUpcomingForm = ({
  errors,
  exDate,
  expectedReinvestmentData,
  onDistributionSubmit,
  onReinvestmentSubmit
}: FundExDateUpcomingFormProps) => {
  const [selectedStep, setSelectedStep] = useState<ExDateTab>(undefined)
  const [completedStep, setCompletedStep] = useState<ExDateTab>(undefined)

  useEffect(() => {
    if (exDate.finalReinvestment.readOnly === true) {
      setCompletedStep(ExDateTab.Complete)
      setSelectedStep(ExDateTab.Reinvestment)
    } else if (exDate.finalDistribution.readOnly === true) {
      setCompletedStep(ExDateTab.Reinvestment)
      setSelectedStep(ExDateTab.Reinvestment)
    } else {
      setSelectedStep(ExDateTab.Distribution)
      setCompletedStep(ExDateTab.Distribution)
    }
  }, [exDate])

  const getState = (currentTabPosition: number) => {
    if (currentTabPosition === completedStep) return 'current'
    const isCompleted = currentTabPosition <= completedStep
    return isCompleted ? 'complete' : 'pending'
  }

  const renderTab = (stepNumber: number, exDateTab: ExDateTab, stepName: string) => (
    <StepTab
      isSelected={selectedStep === exDateTab}
      state={getState(exDateTab)}
      stepNumber={stepNumber}
      onClick={() => setSelectedStep(exDateTab)}
    >
      {stepName}
    </StepTab>
  )

  const StyledReinvestmentFormWrapper = styled(Space)`
    border-top: solid 1px var(--White90);
  `

  const StyledReinvestmentTabStrip = styled(Space)`
    border-bottom: solid 1px var(--White90);
  `

  return (
    <StyledReinvestmentFormWrapper mx={5} py={6}>
      <StyledReinvestmentTabStrip>
        <SimpleTabStrip>
          {renderTab(1, ExDateTab.Distribution, 'Distribution data')}
          {renderTab(2, ExDateTab.Reinvestment, 'Reinvestment units')}
        </SimpleTabStrip>
      </StyledReinvestmentTabStrip>
      <Space mt={3}>
        {selectedStep === ExDateTab.Distribution && (
          <FundsDistribution
            finSwitchDistribution={exDate.finSwitchDistribution}
            finalDistribution={exDate.finalDistribution}
            readOnly={false}
            onSubmit={onDistributionSubmit}
          />
        )}
        {selectedStep === ExDateTab.Reinvestment && (
          <FundsReinvestment
            exDate={exDate.dateStr}
            expectedReinvestmentData={expectedReinvestmentData}
            finSwitchReinvestment={exDate.finSwitchReinvestment}
            finalReinvestment={exDate.finalReinvestment}
            readOnly={false}
            onSubmit={onReinvestmentSubmit}
          />
        )}
        {errors && <ValidationMessage errors={errors} />}
      </Space>
    </StyledReinvestmentFormWrapper>
  )
}

export default FundExDateUpcomingForm
